import { a as autoUpdate, c as computePosition, o as offset } from './floating-ui.dom-BtbMQ4Ls.js';
import { c as createFocusTrap } from './focus-trap.esm-D00doGut.js';

class CartPopover extends HTMLElement {
  subscribers = [];
  cleanup;
  reference = document.querySelector(`[data-floating-reference="${this.id}"]`);
  controllers = document.querySelectorAll(`[aria-controls="${this.id}"]`);
  inactiveClasses = ['opacity-0', 'invisible', '-translate-y-2'];

  connectedCallback() {
    this.initEvents();
    this.initSubscribers();
    this.initFocusTrap();
  }

  disconnectedCallback() {
    this.subscribers.forEach((unsubscribe) => unsubscribe());
  }

  /**
   * Initialize events
   */
  initEvents() {
    this.controllers.forEach((controller) =>
      controller.addEventListener('click', (event) => {
        const { currentTarget } = event;
        currentTarget.ariaExpanded === 'true' ? this.close() : this.open();
      })
    );
    window.addEventListener('scroll', () => this.close());
  }

  /**
   * Initialize subscribers
   */
  initSubscribers() {
    ['cart:added', 'cart:updated', 'cart:change'].forEach((event) => {
      this.subscribers.push(
        subscribe(event, ({ sections, fromQuantityUpdate }) => {
          if (event !== 'cart:change' && !fromQuantityUpdate) this.open();
          this.handleSectionsUpdate(sections);
        })
      );
    });
  }

  /**
   * Initialize focus trap
   */
  initFocusTrap() {
    this.trap = createFocusTrap(this, {
      clickOutsideDeactivates: true,
      initialFocus: false,
      onDeactivate: () => this.close(),
    });
  }

  /**
   * Open cart popover
   */
  open() {
    this.toggleAriaExpanded('true');
    this.classList.remove(...this.inactiveClasses);

    // Wait for transition to complete before activating focus trap
    const handleTransitionEnd = () => {
      this.trap.activate();
      this.removeEventListener('transitionend', handleTransitionEnd);
    };
    this.addEventListener('transitionend', handleTransitionEnd);

    this.cleanup = autoUpdate(this.reference, this, () => {
      computePosition(this.reference, this, {
        placement: 'bottom-end',
        middleware: [offset(16)],
      }).then(({ x, y }) => {
        Object.assign(this.style, {
          left: `${x}px`,
          top: `${y}px`,
        });
      });
    });
  }

  /**
   * Close cart popover
   */
  close() {
    if (!this.cleanup) return;
    this.toggleAriaExpanded('false');
    this.cleanup();
    if (this.trap.active) this.trap.deactivate();
    this.classList.add(...this.inactiveClasses);
  }

  /**
   * Handle sections update
   * @param {Object} sections
   */
  async handleSectionsUpdate(sections) {
    console.log('handleSectionsUpdate');
    const html = new DOMParser().parseFromString(sections['fetch-cart-popover'], 'text/html');
    const fetchedCartPopover = html.querySelector('cart-popover');

    this.innerHTML = fetchedCartPopover.innerHTML;
    const controller = this.querySelector(`[aria-controls="${this.id}"]`);
    controller.ariaExpanded = 'true';
    controller.addEventListener('click', () => this.close());
  }

  toggleAriaExpanded(expanded) {
    this.controllers.forEach((controller) => (controller.ariaExpanded = expanded));
  }
}

customElements.define('cart-popover', CartPopover);
